import { Add, Inventory } from "@mui/icons-material";
import {
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
	Card,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ApiCalls from "services/flick_erp_api_call";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";

export function LeaveBalance({ data, permissionKey, setIsReloadRequest, modulePermissions }) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;
  const [leavesTypes, setLeavesTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [lastYearLeaveBalance, setLastYearLeaveBalance] = useState([]);
  const [addLeaveBalanceDialog, setAddLeaveBalanceDialog] = useState(false);
  const [leaveBalancePayload, setLeaveBalancePayload] = useState({});
  const [leaveTypeValidationError, setLeaveTypeValidationError] = useState("");
  const [balanceValidationError, setBalanceValidationError] = useState("");

  const loggedUser = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    getLeaveTypes();
  }, []);

  useEffect(() => {
    if (!data?.id) return;
    getLeaveBalance();
    getLeaveBalanceLastYear();
  }, [data?.id]);

  async function getLeaveBalance() {
    const res = await ApiCalls.getEmployeeLeaveBalance(data?.id);
    if (res.success && res.code == 200) setLeaveBalance(res.data);
  }

  async function getLeaveBalanceLastYear() {
    const res = await ApiCalls.getEmployeeLeaveBalanceLastYear(data?.id);
    if (res.success && res.code === 200) setLastYearLeaveBalance(res.data);
  }

  async function getLeaveTypes() {
    const res = await ApiCalls.getLeavesTypes();
    if (res.success && res.code == 200) setLeavesTypes(res.data);
  }

  async function postLeaveBalance() {
    setLeaveTypeValidationError("");
    setBalanceValidationError("");
    let isValid = true;

    if (!leaveBalancePayload.leave_type) {
      setLeaveTypeValidationError("Leave Type is required");
      isValid = false;
    }
    if (!leaveBalancePayload.balance || isNaN(leaveBalancePayload.balance)) {
      setBalanceValidationError("Balance must be a valid number");
      isValid = false;
    }
    if (!isValid) return;

    const payload = {
      balance: leaveBalancePayload?.balance,
      org: loggedUser?.org_id,
      employee: data?.id,
      leave_type: leaveBalancePayload?.leave_type,
    };
    const res = await ApiCalls.postLeaveBalance(payload, false);
    if (res.success && res.code == 201) {
      setAddLeaveBalanceDialog(false);
      getLeaveBalance();
    }
  }

  async function patchLeaveBalance() {
    const payload = {
      ...leaveBalancePayload,
      leave_type: leaveBalancePayload.leave_type.id,
    };
    const res = await ApiCalls.patchLeaveBalance(payload, leaveBalancePayload.id, false);
    if (res.success && res.code == 200) {
      setIsReloadRequest(true);
      setAddLeaveBalanceDialog(false);
    }
  }

  const leavesData = [
    { year: "Current Year", ...leaveBalance },
    { year: "Last Year", ...lastYearLeaveBalance },
  ];

  const summaryKeys = [
    { key: "casual_leave", label: "Casual Leave", tone: colors.info.main },
    { key: "earned_leave", label: "Earned Leave", tone: colors.warning.focus },
    { key: "un_paid", label: "Unpaid Leave", tone: colors.error.main },
  ];
  const ui = {
    card: darkMode
      ? `linear-gradient(135deg, ${rgba(palette.background.muted, 0.88)} 0%, ${rgba(palette.background.card, 0.98)} 100%)`
      : `linear-gradient(135deg, ${rgba(colors.background.elevated, 0.82)} 0%, ${rgba(colors.white.main, 0.98)} 100%)`,
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.08),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.text.main,
  };

  return (
    <Card>
      <MDBox p={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
					<MDBox
						//p={3}
						lineHeight={1}
						sx={{ borderBottom: `1px solid ${rgba(colors.secondary.main, 0.08)}`, display: "flex", gap: 3 }}
					>
						<MDBox
							sx={{
								width: 48,
								height: 48,
								borderRadius: "16px",
								display: "grid",
								placeItems: "center",
								background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
								color: colors.white.main,
							}}
						>
							<Inventory />
						</MDBox>
						<MDBox>
            <MDTypography variant="h5">
              Leave Balance
            </MDTypography>
            <MDTypography variant="body2" sx={{ mt: 0.35, color: ui.body }}>
              Current and previous year leave allocations.
            </MDTypography>
          </MDBox>
					</MDBox>

          {loggedUser?.permissions_all[permissionKey]?.includes(
            modulePermissions?.leave_balance_create_permission
          ) && (
            <IconButton
              onClick={() => {
                setLeaveTypeValidationError("");
                setBalanceValidationError("");
                setLeaveBalancePayload({ request: "create" });
                setAddLeaveBalanceDialog(true);
              }}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                backgroundColor: rgba(colors.info.main, 0.12),
                color: colors.info.focus,
              }}
            >
              <Add />
            </IconButton>
          )}
        </Stack>

        {(loggedUser?.permissions_all[permissionKey]?.includes(
          modulePermissions?.leave_balance_list_permission
        ) ||
          loggedUser?.permissions_all[permissionKey]?.includes(
            modulePermissions?.leave_balance_owner_list_permission
          )) ? (
          <Grid container spacing={2}>
            {leavesData.map((yearData) => (
              <Grid item xs={12} md={6} key={yearData.year}>
                <MDBox
                  sx={{
                    p: 2,
                    borderRadius: "22px",
                    background: ui.card,
                    border: `1px solid ${ui.border}`,
                    boxShadow: darkMode ? "0 10px 24px rgba(2, 6, 23, 0.22)" : `0 10px 24px ${rgba(colors.dark.main, 0.05)}`,
                    height: "100%",
                  }}
                >
                  <MDTypography variant="button" sx={{ color: ui.body }}>
                    {yearData.year}
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ color: ui.title, mt: 0.5, mb: 2 }}>
                    Leave Summary
                  </MDTypography>

                  <Grid container spacing={1.5}>
                    {summaryKeys.map((item) => (
                      <Grid item xs={12} key={item.key}>
                        <MDBox
                          sx={{
                            p: 1.3,
                            borderRadius: "18px",
                            backgroundColor: rgba(item.tone, 0.08),
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <MDTypography variant="button" sx={{ color: ui.title }}>
                            {item.label}
                          </MDTypography>
                          <MDTypography variant="h6" sx={{ color: item.tone }}>
                            {yearData?.[item.key] || 0}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        ) : (
          <MDBox p={2}>
            <MDTypography>Not permitted to view leave balance list.</MDTypography>
          </MDBox>
        )}
      </MDBox>

      <Dialog
        open={addLeaveBalanceDialog}
        onClose={() => setAddLeaveBalanceDialog(false)}
        PaperProps={{
          sx: {
            width: "min(520px, 92vw)",
            borderRadius: "24px",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ color: ui.title, fontWeight: 700 }}>
          Add Leave Balance
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" alignItems="center" pt={2}>
            <Grid container spacing={2} alignItems="flex-end" p={1}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={leavesTypes}
                  value={leavesTypes.find((e) => e?.id == leaveBalancePayload?.leave_type?.id)}
                  onChange={(event, values) => {
                    setLeaveBalancePayload((state) => ({
                      ...state,
                      leave_type: values.id,
                    }));
                    setLeaveTypeValidationError("");
                  }}
                  getOptionLabel={(v) => v.name}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      variant="standard"
                      label="Leave Type"
                      {...params}
                      placeholder="Select Leave Type"
                      InputLabelProps={{ shrink: true }}
                      error={!!leaveTypeValidationError}
                      helperText={leaveTypeValidationError}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Balance"
                  fullWidth
                  variant="standard"
                  value={leaveBalancePayload?.balance || ""}
                  onChange={(e) => {
                    setLeaveBalancePayload((state) => ({
                      ...state,
                      balance: e.target.value,
                    }));
                    setBalanceValidationError("");
                  }}
                  error={!!balanceValidationError}
                  helperText={balanceValidationError}
                />
              </Grid>
            </Grid>
            <MDButton
              color="info"
              onClick={() => {
                leaveBalancePayload.request == "create" ? postLeaveBalance() : patchLeaveBalance();
              }}
            >
              {leaveBalancePayload.request == "create" ? "Add" : "Edit"}
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
